import React from 'react';
import { Tooltip } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Link } from 'react-router-dom';
import {
  Datagrid,
  List,
  NumberField,
  Pagination,
  ReferenceField,
  ReferenceInput,
  Resource,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin';

const ratioBandHelp = (metric, keyPrefix, valueDescription = `${metric} value`) =>
  `Classifies the ${valueDescription} using thresholds from Circularity Defaults. ` +
  `Low is below band_${keyPrefix}_low_upper; Medium is at or above that threshold ` +
  `and below band_${keyPrefix}_medium_upper; High is at or above the medium threshold. ` +
  'Default boundaries are 33% and 66%.';

export const CIRCULARITY_ARTICLE_INSPECTOR_FIELDS = [
  {
    source: 'mciBand',
    sortable: false,
    helpText: ratioBandHelp('MCI', 'mci'),
  },
  {
    source: 'recycledContentBand',
    sortable: false,
    helpText: ratioBandHelp('recycled content', 'recycled_content'),
  },
  {
    source: 'recyclabilityBand',
    sortable: false,
    helpText: ratioBandHelp('recyclability', 'recyclability'),
  },
  {
    source: 'eolCircularityBand',
    sortable: false,
    helpText: ratioBandHelp('end-of-life circularity', 'eol'),
  },
  { source: 'collectionRateNormalized' },
  {
    source: 'collectionRateNormalizedBand',
    sortable: false,
    helpText: ratioBandHelp(
      'collection',
      'collection',
      'normalized collection rate after the approved default is applied',
    ),
  },
  { source: 'collectionRateDeclared', sortable: false },
  {
    source: 'collectionRateDeclaredBand',
    sortable: false,
    helpText: ratioBandHelp(
      'collection',
      'collection',
      'collection rate declared on the article before normalization',
    ),
  },
  { source: 'eprAverage', sortable: false },
];

const fieldDefinition = (source) =>
  CIRCULARITY_ARTICLE_INSPECTOR_FIELDS.find((field) => field.source === source);

export const BandHeader = ({ label, source }) => {
  const helpText = fieldDefinition(source)?.helpText;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span>{label}</span>
      <Tooltip title={helpText} enterTouchDelay={0}>
        <span
          aria-label={`About ${label}`}
          role="button"
          tabIndex={0}
          style={{ display: 'inline-flex', cursor: 'help' }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </span>
      </Tooltip>
    </span>
  );
};

export const CircularityRecordEditButton = ({ record }) => {
  if (!record?.circularityRecordId) {
    return <span aria-label="No stored circularity record">—</span>;
  }

  return (
    <Link
      to={`/articleImpactCircularity/${record.circularityRecordId}`}
      aria-label="Edit stored circularity record"
      onClick={(event) => event.stopPropagation()}
      style={{ color: '#3f51b5', fontWeight: 500, textDecoration: 'none' }}
    >
      Edit stored record
    </Link>
  );
};

const articleFilters = [
  <ReferenceInput
    key="project"
    source="projectId"
    reference="projects"
    label="Project"
    filterToQuery={(query) => ({ name: query })}
  >
    <SelectInput optionText="name" />
  </ReferenceInput>,
  <TextInput key="collection" source="collection" label="Collection" alwaysOn />,
  <TextInput key="name" source="name" label="Article name" alwaysOn />,
  <TextInput
    key="reference"
    source="reference"
    label="Article reference"
    alwaysOn
  />,
];

export const CircularityArticleMetricsList = (props) => (
  <List
    {...props}
    filters={articleFilters}
    sort={{ field: 'articleName', order: 'ASC' }}
    pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    bulkActionButtons={false}
    title="Circularity Article Metrics"
  >
    <Datagrid>
            <TextField source="articleName" label="Article" emptyText="—" />
            <TextField
              source="articleReference"
              label="Reference"
              emptyText="—"
            />
            <ReferenceField
              source="projectId"
              reference="projects"
              label="Project"
              link={false}
              sortable={false}
            >
              <TextField source="name" />
            </ReferenceField>
            <TextField source="collection" emptyText="—" />
            <NumberField source="amount" label="Amount (units)" />
            <NumberField source="weightKg" label="Product mass (kg/unit)" />
            <NumberField
              source="mci"
              label="MCI"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <TextField source="mciBand" label={<BandHeader label="MCI band" source="mciBand" />} emptyText="—" sortable={false} />
            <NumberField source="recycledKg" label="Recycled (kg/unit)" />
            <NumberField
              source="recycledContentRatio"
              label="Recycled content"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <TextField source="recycledContentBand" label={<BandHeader label="Recycled content band" source="recycledContentBand" />} emptyText="—" sortable={false} />
            <NumberField
              source="verifiedRecycledContentDeclared"
              label="Declared recycled content"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <NumberField
              source="verifiedRecycledContentCertified"
              label="Certified recycled content"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <NumberField source="wasteKg" label="Waste (kg/unit)" />
            <NumberField
              source="recyclability"
              label="Recyclability"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <TextField source="recyclabilityBand" label={<BandHeader label="Recyclability band" source="recyclabilityBand" />} emptyText="—" sortable={false} />
            <NumberField
              source="eolCircularity"
              label="EoL circularity"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <TextField source="eolCircularityBand" label={<BandHeader label="EoL circularity band" source="eolCircularityBand" />} emptyText="—" sortable={false} />
            <NumberField
              source="collectionRateNormalized"
              label="Normalized collection rate"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <TextField source="collectionRateNormalizedBand" label={<BandHeader label="Normalized collection band" source="collectionRateNormalizedBand" />} emptyText="—" sortable={false} />
            <NumberField
              source="collectionRateDeclared"
              label="Declared collection rate"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
              sortable={false}
            />
            <TextField source="collectionRateDeclaredBand" label={<BandHeader label="Declared collection band" source="collectionRateDeclaredBand" />} emptyText="—" sortable={false} />
            <NumberField
              source="eprAverage"
              label="Phase-one EPR average"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
              sortable={false}
            />
            <NumberField
              source="durability"
              label="Durability"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <NumberField
              source="repairability"
              label="Repairability (0–6)"
              emptyText="—"
            />
            <NumberField
              source="recoveryRateDefaultApplied"
              label="Applied EPR default"
              options={{ style: 'percent', maximumFractionDigits: 2 }}
              emptyText="—"
            />
            <TextField
              source="eprProvenance.source"
              label="EPR provenance"
              emptyText="—"
              sortable={false}
            />
            <TextField
              source="calculationType"
              label="Calculation type"
              emptyText="—"
            />
            <CircularityRecordEditButton label="Stored record" sortable={false} />
    </Datagrid>
  </List>
);

const circularityArticleMetricsResource = () => (
  <Resource
    name="articleCircularityMetrics"
    options={{
      label: 'Circularity Article Metrics',
      menuGroup: 'Article Impact Sub',
    }}
    list={CircularityArticleMetricsList}
  />
);

export default circularityArticleMetricsResource;
